import { toneBadgeVariant, type Tone } from "@/modules/shared/status";

export const DEFAULT_TICKET_INBOX_EMAIL =
  import.meta.env.VITE_TICKET_INBOX_EMAIL?.trim() || "";

export const TICKET_STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "needs_reply", label: "Needs reply" },
  { id: "new", label: "New" },
  { id: "open", label: "Open" },
  { id: "waiting", label: "Waiting" },
  { id: "resolved", label: "Resolved" },
] as const;

/** Built-in filter chips plus org custom status slugs (`status@eq`). */
export type TicketStatusFilterId =
  | (typeof TICKET_STATUS_FILTERS)[number]["id"]
  | (string & {});

/**
 * Product queue base statuses: new + open (not waiting/resolved).
 * Merged tickets are excluded by list filters.
 *
 * True “needs reply” also requires no staff outbound after the latest inbound
 * (see `ticketNeedsStaffReply`). Prefer `last_inbound_at` vs `last_outbound_at`
 * (trigger-maintained). Server inbox filter stays status-only; client refines.
 * Custom statuses never qualify for needs_reply.
 */
export const NEEDS_REPLY_TICKET_STATUSES = ["new", "open"] as const;

export type TicketNeedsReplySource = {
  status?: string | null;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  updated_at?: string | null;
  /**
   * When known from loaded ticket_messages (prefer over timestamp heuristic).
   * Only inbound/outbound — ignore internal notes.
   */
  lastClientFacingDirection?: "inbound" | "outbound" | null;
};

/**
 * Needs reply = status new|open AND (latest client-facing message is inbound,
 * or never had outbound after inbound). Waiting/resolved never qualify.
 *
 * Prefer `lastClientFacingDirection` when messages are loaded. Otherwise use
 * `last_inbound_at` vs `last_outbound_at` (denormalized by trigger). Falls back
 * to status-only when outbound clock is missing.
 */
export const ticketNeedsStaffReply = (
  ticket: TicketNeedsReplySource,
): boolean => {
  const status = ticket.status?.trim() ?? "";
  if (!(NEEDS_REPLY_TICKET_STATUSES as readonly string[]).includes(status)) {
    return false;
  }

  if (
    ticket.lastClientFacingDirection === "inbound" ||
    ticket.lastClientFacingDirection === "outbound"
  ) {
    return ticket.lastClientFacingDirection === "inbound";
  }

  const inboundAt = ticket.last_inbound_at?.trim() || null;
  const outboundAt = ticket.last_outbound_at?.trim() || null;

  if (!inboundAt) {
    // No inbound on record — treat as awaiting staff (brand-new ticket).
    return true;
  }

  if (!outboundAt) return true;

  return Date.parse(inboundAt) >= Date.parse(outboundAt);
};

export const BUILTIN_TICKET_STATUS_CHOICES = [
  { id: "new", name: "New" },
  { id: "open", name: "Open" },
  { id: "waiting", name: "Waiting" },
  { id: "resolved", name: "Resolved" },
] as const;

export type TicketStatusChoice = { id: string; name: string };

export const ticketStatusChoices = (
  custom: Array<{ value: string; label: string; active: boolean }> = [],
): TicketStatusChoice[] => {
  const seen = new Set<string>(
    BUILTIN_TICKET_STATUS_CHOICES.map((choice) => choice.id),
  );
  const extras: TicketStatusChoice[] = [];
  for (const status of custom) {
    const id = status.value?.trim();
    if (!status.active || !id || seen.has(id)) continue;
    seen.add(id);
    extras.push({ id, name: status.label?.trim() || id });
  }
  return [
    ...BUILTIN_TICKET_STATUS_CHOICES.map((choice) => ({
      id: choice.id,
      name: choice.name,
    })),
    ...extras,
  ];
};

const BUILTIN_TICKET_STATUS_IDS = new Set<string>(
  BUILTIN_TICKET_STATUS_CHOICES.map((choice) => choice.id),
);

/**
 * Inbox/Overview filter menu: All + Needs reply + builtins + active customs.
 * Customs come from `ticketStatusChoices` / `useTicketStatusChoices`.
 */
export const ticketStatusFilterOptions = (
  choices: TicketStatusChoice[] = [],
): Array<{ id: string; label: string }> => {
  const base = TICKET_STATUS_FILTERS.map((item) => ({
    id: item.id,
    label: item.label,
  }));
  const seen = new Set<string>(base.map((item) => item.id));
  const extras: Array<{ id: string; label: string }> = [];
  for (const choice of choices) {
    const id = choice.id?.trim();
    if (!id || seen.has(id) || BUILTIN_TICKET_STATUS_IDS.has(id)) continue;
    seen.add(id);
    extras.push({ id, label: choice.name?.trim() || id });
  }
  return [...base, ...extras];
};

/** Matches Overview/Kanban/Types/Table client filters. "all" includes every status. */
export const matchesTicketOverviewStatus = (
  ticketOrStatus: string | null | undefined | TicketNeedsReplySource,
  filter: string,
) => {
  if (filter === "all") return true;
  if (filter === "needs_reply") {
    if (ticketOrStatus != null && typeof ticketOrStatus === "object") {
      return ticketNeedsStaffReply(ticketOrStatus);
    }
    const status =
      typeof ticketOrStatus === "string" ? ticketOrStatus.trim() : "";
    // Status-only fallback when callers lack timestamps/direction.
    return (NEEDS_REPLY_TICKET_STATUSES as readonly string[]).includes(status);
  }
  const status =
    ticketOrStatus != null && typeof ticketOrStatus === "object"
      ? (ticketOrStatus.status?.trim() ?? "")
      : (ticketOrStatus?.trim() ?? "");
  return status === filter;
};

export const ticketStatusLabel = (status: string) => status.replace(/_/g, " ");

export const ticketStatusTone = (status: string): Tone => {
  switch (status) {
    case "new":
      return "info";
    case "open":
      return "success";
    case "waiting":
      return "warning";
    default:
      return "muted";
  }
};

export const ticketStatusVariant = (status: string) =>
  toneBadgeVariant(ticketStatusTone(status));

export const formatTicketRelativeTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 1) return "just now";
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }
  return date.toLocaleDateString();
};
